/** @jsxImportSource @emotion/react */
import { Expression, Map as MBMap, LineLayer } from 'mapbox-gl'
import { byAssetIdIn } from '../RiskMapView.utilities'
import { MapLayerStyle } from '@app/types/mapTypes'

export interface LineLayerProps {
  map: MBMap
  layerStyle: MapLayerStyle
  id: string
  beforeId: string
  idAttribute?: string
  isVisible: boolean
  sourceLayer: string
  vulnerabilityData?: Map<string, number>
  vulnerabilityPalette?: string[]
  colorExpression?: Expression
  lineOpacityExpression?: Expression
  assetIds?: { [asset_id: number]: boolean }
  sortKeyExpression?: Expression
  hasFilters?: boolean
  shouldUpdate?: boolean
}

/**
 * Creates a line layer.
 * Styling is proivded either via colorExpression or with
 * vulnerabilityData & vulnerabilityPalette.
 */
export const addLineLayer = ({
  map,
  layerStyle,
  id,
  beforeId,
  idAttribute,
  isVisible,
  sourceLayer,
  vulnerabilityData,
  vulnerabilityPalette,
  colorExpression,
  lineOpacityExpression,
  assetIds,
  sortKeyExpression,
  hasFilters = false,
  shouldUpdate = false,
}: LineLayerProps) => {
  const layerData: LineLayer = {
    id,
    type: 'line',
    source: id,
    'source-layer': sourceLayer,
    layout: {
      visibility: isVisible ? 'visible' : 'none',
      'line-join': 'round',
      'line-cap': 'round',
      'line-sort-key': sortKeyExpression || 0,
      ...layerStyle.layout,
    },
    paint: {
      'line-width': ['interpolate', ['exponential', 2], ['zoom'], 1, 0.11, 15, 7, 20, 30],
      'line-opacity': lineOpacityExpression,
      'line-color': colorExpression || [
        'to-color',
        [
          'at',
          ['coalesce', ['feature-state', 'vulnerability_style'], 0],
          ['literal', vulnerabilityPalette],
        ],
      ],
      ...layerStyle.paint,
    },
  }

  if (shouldUpdate) {
    if (layerData.paint) {
      for (const paintProp of Object.entries(layerData.paint)) {
        map.setPaintProperty(id, paintProp[0] as any, paintProp[1])
      }
    }
    if (layerData.layout) {
      for (const layoutProp of Object.entries(layerData.layout)) {
        map.setLayoutProperty(id, layoutProp[0] as any, layoutProp[1])
      }
    }
    if (!hasFilters && !assetIds) map.setFilter(id, null)
  } else {
    map.addSource(id, {
      type: 'vector',
      url: `mapbox://${id}`,
      ...(idAttribute && { promoteId: idAttribute }),
    })
    map.addLayer(layerData, beforeId)
  }
  if (hasFilters && assetIds) {
    const visibilityExpression: Expression = ['boolean', byAssetIdIn(assetIds)]
    map.setFilter(id, visibilityExpression)
  }

  // Replace feature state:
  map.removeFeatureState({
    source: id,
    sourceLayer: sourceLayer,
  })
  if (vulnerabilityData) {
    for (const [assetId, vulnerabilityStyle] of vulnerabilityData) {
      map.setFeatureState(
        {
          source: id,
          sourceLayer: sourceLayer,
          id: parseInt(assetId),
        },
        {
          vulnerability_style: vulnerabilityStyle,
        },
      )
    }
  }
}
